// frontend/src/components/WorkspaceSwitcher.js
//
// Shows the current workspace and lets the user change it.
//
// This is not decoration: every write names a workspace, so the user must be
// able to see which one they are in before they type. A chat UI that hides
// this would eventually put a private conversation in a client's workspace.
//
// Two presentations, because the desktop pill nav and the mobile bottom bar are
// different idioms -- but BOTH must exist. Rendering only the desktop one broke
// this component's own rationale on phones (David, reviewing !12): the
// selection persists per user in localStorage, so someone who last used a
// client workspace on desktop opens their phone to an ordinary chat screen with
// no indication of scope, and mobile is precisely where a quick unconsidered
// message gets typed. On the personal instance the workspaces are counsel /
// business / fan-fiction / track-exposure -- the case where filing something in
// the wrong place matters most.
import React from 'react';
import {
    Button,
    Icon,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Text,
    Tooltip,
    VStack,
} from '@chakra-ui/react';
import { ChevronDownIcon, CheckIcon } from '@chakra-ui/icons';
import { MdWorkspaces } from 'react-icons/md';

import { useWorkspace } from '../contexts/WorkspaceContext';

// Shared: the list of workspaces, with the current one ticked.
const WorkspaceMenuList = ({ workspaces, currentWorkspaceId, selectWorkspace }) => (
    <MenuList>
        {workspaces.map((workspace) => (
            <MenuItem
                key={workspace.id}
                onClick={() => selectWorkspace(workspace.id)}
                icon={
                    workspace.id === currentWorkspaceId ? <CheckIcon /> : undefined
                }
                fontWeight={
                    workspace.id === currentWorkspaceId ? 'semibold' : 'normal'
                }
            >
                {workspace.name}
            </MenuItem>
        ))}
    </MenuList>
);

// ─── Desktop: inline pill button ──────────────────────────────────────────────

const WorkspaceSwitcher = ({ size = 'sm', borderRadius = '0' }) => {
    const { workspaces, currentWorkspace, currentWorkspaceId, selectWorkspace } =
        useWorkspace();

    if (!workspaces.length) return null;

    const label = currentWorkspace?.name || 'Workspace';

    // With a single workspace there is nothing to choose. Show it as a static
    // label rather than a dead menu -- most instances start this way, right
    // after the migration.
    if (workspaces.length === 1) {
        return (
            <Tooltip label={`Workspace: ${label}`} openDelay={400}>
                <Button
                    size={size}
                    variant="ghost"
                    leftIcon={<Icon as={MdWorkspaces} boxSize="16px" />}
                    borderRadius={borderRadius}
                    px={3}
                    fontSize="sm"
                    cursor="default"
                    _hover={{ bg: 'transparent' }}
                    _active={{ bg: 'transparent' }}
                >
                    <Text noOfLines={1} maxW="160px">{label}</Text>
                </Button>
            </Tooltip>
        );
    }

    return (
        <Menu>
            <MenuButton
                as={Button}
                size={size}
                variant="ghost"
                leftIcon={<Icon as={MdWorkspaces} boxSize="16px" />}
                rightIcon={<ChevronDownIcon />}
                borderRadius={borderRadius}
                px={3}
                fontSize="sm"
            >
                <Text noOfLines={1} maxW="160px">{label}</Text>
            </MenuButton>
            <WorkspaceMenuList
                workspaces={workspaces}
                currentWorkspaceId={currentWorkspaceId}
                selectWorkspace={selectWorkspace}
            />
        </Menu>
    );
};

// ─── Mobile: bottom-bar item ──────────────────────────────────────────────────
//
// Icon above a truncated label, matching the other bottom-bar buttons. The
// workspace name is always visible, so scope is never implicit.

const mobileItemStyles = {
    variant: 'ghost',
    size: 'sm',
    h: 'auto',
    py: 1,
    minW: '64px',
    maxW: '96px',
    px: 1,
};

export const MobileWorkspaceSwitcher = () => {
    const { workspaces, currentWorkspace, currentWorkspaceId, selectWorkspace } =
        useWorkspace();

    if (!workspaces.length) return null;

    const label = currentWorkspace?.name || 'Workspace';

    const content = (
        <VStack spacing="1px">
            <Icon as={MdWorkspaces} boxSize="20px" />
            <Text fontSize="10px" noOfLines={1} maxW="88px" fontWeight="500">
                {label}
            </Text>
        </VStack>
    );

    // Single workspace: display only. Still shown, because "which workspace am
    // I in" must be answerable at a glance even when it cannot change.
    if (workspaces.length === 1) {
        return (
            <Button
                {...mobileItemStyles}
                aria-label={`Workspace: ${label}`}
                cursor="default"
                _hover={{ bg: 'transparent' }}
                _active={{ bg: 'transparent' }}
            >
                {content}
            </Button>
        );
    }

    return (
        <Menu placement="top">
            <MenuButton
                as={Button}
                {...mobileItemStyles}
                aria-label={`Workspace: ${label}. Tap to switch.`}
            >
                {content}
            </MenuButton>
            <WorkspaceMenuList
                workspaces={workspaces}
                currentWorkspaceId={currentWorkspaceId}
                selectWorkspace={selectWorkspace}
            />
        </Menu>
    );
};

export default WorkspaceSwitcher;
